// ---------------------------------------------------------------------------
// TimelinePane — pane "Linha do tempo" do ProgressDrawerV3.
// ---------------------------------------------------------------------------
// Lista cronologica reversa (futuro -> agora -> passado) dos eventos do
// processamento DA IMPLEMENTACAO no mes selecionado. 1 evento = 1 job
// (sem agregacao). O usuario filtra por categoria via badges no topo:
//
//   - "Envio de inputs"   (job_type STRUCTURE_CHECK; futuramente uploads)
//   - "Cruzamentos de dados" (job_type CROSS_LINKING)
//   - "Cálculos"          (job_type BLOCK)
//
// Eventos com ``kind`` especiais (``now``, ``future-waiting``) sao
// sempre visiveis — o filtro so atua sobre ``kind === "past"``.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Schedule from "@material-ui/icons/Schedule";
import {
  whiteColor,
  grayColor,
  successColor,
  warningColor,
  dangerColor,
  blendColors,
} from "../../../../../assets/jss/material-dashboard-pro-react";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useProgressStyles from "../styles";
import { useOverlayScrollStyles } from "../../styles";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { fetchInputsV3Timeline } from "../../../../../services/inputsV3Service";
import PaneErrorBanner from "../../drawer/panes/PaneErrorBanner";
import DrawerPaneLoadingLayout from "../../DrawerPaneLoadingLayout";

const FILTERS = [
  { id: "inputs", label: "Envio de inputs" },
  { id: "crosslinks", label: "Cruzamentos de dados" },
  { id: "blocks", label: "Cálculos" },
];

// Badges por categoria removidos (mai/2026): apos os filtros virarem
// excludentes (1 categoria ativa por vez), o badge "input/cruzamento/
// calculo" ao lado de cada linha virou redundancia visual — toda a
// lista visivel pertence a' mesma categoria do chip ativo no topo do
// pane.

// Fallback puro: devolve o ``meta_name`` cru (snake_case) quando o
// backend nao consegue resolver ``input_name[lang]``/``display_name``
// do snapshot. Antes este helper convertia para Title Case ("tarifario
// ado" -> "Tarifario Ado") — divergia do resto da UI, que mostra
// ``input.input_name[lang]`` (ja escrito como o time de produto quis)
// ou cai no ``meta_name`` cru.
function metaFallback(meta) {
  return meta ? String(meta) : "Input";
}

function stripExecutionTime(text) {
  if (!text) return "";
  return String(text)
    .replace(/\s+conclu[ií]do em\s+[^—]+/i, " concluído")
    .replace(/\s+tempo total:\s*[^—]+/i, "");
}

// Formata um timestamp ISO no fuso horario local do usuario. Backend
// roda em UTC; usar ``toLocaleString`` evita render incorreto para
// quem nao esta no mesmo TZ do servidor.
function formatNowLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const time = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    const date = d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${time} · ${date}`;
  } catch (_e) {
    return "";
  }
}

function EventItem({ classes, event, clientColor, clientColorSoft }) {
  if (event.kind === "now") {
    const display = event.now_iso
      ? formatNowLocal(event.now_iso)
      : event.content;
    return (
      <div
        className={`${classes.event} ${classes.nowMarker}`}
        style={{
          background: blendColors(clientColor, whiteColor, 0.04),
          borderTop: `1px solid ${blendColors(clientColor, whiteColor, 0.08)}`,
          borderBottom: `1px solid ${blendColors(clientColor, whiteColor, 0.08)}`,
        }}
      >
        <span
          className={`${classes.eventTime} ${classes.nowTime}`}
          style={{ color: clientColor }}
        >
          {event.time}
        </span>
        <span
          className={classes.eventDot}
          style={{
            background: clientColor,
            boxShadow: `0 0 0 3px ${clientColorSoft}`,
          }}
        />
        <div className={classes.eventText}>
          <strong style={{ color: grayColor[2], fontSize: 11.5 }}>
            {display}
          </strong>
        </div>
      </div>
    );
  }
  if (event.kind === "future-waiting") {
    return (
      <div className={`${classes.event} ${classes.eventFuture}`}>
        <span className={classes.eventTime}>{event.time}</span>
        <span className={`${classes.eventDot} ${classes.eventDotWaiting}`} />
        <div className={classes.eventText}>
          {event.who && <span className={classes.eventWho}>{event.who}</span>}
          <span className={classes.eventWhat}>{event.what}</span>
        </div>
      </div>
    );
  }
  const displayWhat = stripExecutionTime(event.what);
  // A-005 / Phase 5.2: eventos "superados" (ex.: erro antigo de um
  // bloco que ja foi re-rodado com sucesso) ganham opacidade reduzida
  // + decoracao "tachado" no ``what``. NAO sao escondidos — manter o
  // historico e' uma das premissas; apenas comunicar visualmente que
  // nao representam mais o estado atual.
  const supersededStyle = event.superseded ? { opacity: 0.45 } : undefined;
  return (
    <div className={classes.event} style={supersededStyle}>
      <span className={classes.eventTime}>{event.time}</span>
      <span className={classes.eventDot} style={{ background: event.dot }} />
      <div className={classes.eventText}>
        {event.who && <span className={classes.eventWho}>{event.who} </span>}
        <span
          className={classes.eventWhat}
          style={
            event.superseded ? { textDecoration: "line-through" } : undefined
          }
        >
          {event.inputName && (
            <>
              <strong style={{ color: grayColor[2] }}>{event.inputName}</strong>{" "}
            </>
          )}
          {displayWhat}
        </span>
      </div>
    </div>
  );
}

EventItem.propTypes = {
  classes: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  clientColor: PropTypes.string.isRequired,
  clientColorSoft: PropTypes.string.isRequired,
};

// Mapa do payload do ``InputsV3TimelineView`` para a shape que o
// ``EventItem`` ja consome. Mantemos as 3 categorias do filtro
// (inputs / crosslinks / blocks) — uploads e parsings caem em
// "inputs"; decisoes tambem (e' uma pergunta sobre um input);
// runs de bloco caem em "blocks". Cross-links propriamente ditos
// nao aparecem ainda (o backend nao publica eventos de xlink), mas
// o filtro continua disponivel para o futuro.
function dotForSeverity(state, severity, supersededById) {
  if (supersededById) return grayColor[5];
  if (state === "success") return successColor[3];
  if (state === "warning") return warningColor[2];
  if (state === "error" || state === "system_error") return dangerColor[2];
  if (severity === 3) return dangerColor[2];
  if (severity === 2) return warningColor[2];
  return successColor[3];
}

// Mapeia o ``ev.type`` cru do ``InputsV3TimelineView`` (backend) para
// uma das 3 categorias do filtro de cima do pane (``inputs`` /
// ``crosslinks`` / ``blocks``).
//
// Editorial (mai/2026):
//   - ``inputs`` isola APENAS o ato de "envio" (``input_uploaded``).
//   - ``crosslinks`` mostra runs reais de cross-link (``xlink_run``)
//     com o display_name canonico e o % de cobertura. Decisoes
//     (pergunta em aberto / respondida) tambem caem aqui porque sao
//     sempre disparadas por questoes de cruzamento. ``input_parsed``
//     foi removido do backend (era ruido — "Leitura concluida" nao
//     comunica nada sobre o cruzamento).
//   - ``blocks`` agrupa runs de calculo (``block_run``).
function categoryForType(eventType) {
  switch (eventType) {
    case "input_uploaded":
    case "sensitive_data":
      return "inputs";
    case "block_run":
      return "blocks";
    case "xlink_run":
    case "csl_evaluated":
    case "decision_opened":
    case "decision_answered":
      return "crosslinks";
    default:
      return "crosslinks";
  }
}

// Formata "% cruzado" — 1 casa decimal quando frac, sem casas quando
// inteiro. ``null`` quando o backend nao computou cobertura.
function formatCoveragePct(pct) {
  if (typeof pct !== "number" || Number.isNaN(pct)) return null;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === Math.floor(rounded)) return `${rounded.toFixed(0)}%`;
  return `${rounded.toFixed(1)}%`;
}

// Retorna ``{ inputName, whatSuffix }``. ``inputName`` e' o nome da
// entidade renderizado em ``<strong>`` pelo ``EventItem`` (input,
// cross-link ou bloco). ``whatSuffix`` e' o complemento sem negrito.
// Para eventos sem entidade associavel ("now"/"future-waiting" etc.)
// cai em ``{ inputName: null, whatSuffix: ev.label }``.
function labelForTimelineEvent(ev) {
  if (ev.type === "input_uploaded") {
    const inputName =
      ev.related_input_label || metaFallback(ev.related_input || "");
    // ``who`` e' a fonte de verdade da atribuicao. O backend ja
    // resolve auto_reuse / inherited buscando o uploader original do
    // historico do meta — quando ainda assim nao ha humano associavel
    // (ex.: input nunca foi enviado por alguem, so existiu como
    // replica de sistema), caimos no fallback "enviado via sistema".
    const suffix = ev.who ? `enviado por ${ev.who}` : "enviado via sistema";
    return { inputName, whatSuffix: `— ${suffix}` };
  }
  if (ev.type === "sensitive_data") {
    const inputName =
      ev.related_input_label || metaFallback(ev.related_input || "");
    return {
      inputName,
      whatSuffix:
        "— dados sensíveis: original não armazenado, extract anonimizado",
    };
  }
  if (ev.type === "xlink_run") {
    const xlinkName =
      ev.related_xlink_label || metaFallback(ev.related_xlink || "");
    const verb = String(ev.label || "concluído").toLowerCase();
    const pct = formatCoveragePct(ev.coverage_pct);
    const suffix = pct ? `— ${verb} (${pct} cruzado)` : `— ${verb}`;
    return { inputName: xlinkName, whatSuffix: suffix };
  }
  if (ev.type === "block_run") {
    const blockName =
      ev.related_block_label || metaFallback(ev.related_block || "");
    const verb = String(ev.label || "concluído").toLowerCase();
    return { inputName: blockName, whatSuffix: `— ${verb}` };
  }
  if (ev.type === "decision_opened" || ev.type === "decision_answered") {
    // Ancoragem da decisao (rev. 2026-05-26):
    //   - ``input_parse``/``input_row_edit``: ``related_input`` traz
    //     o input. Mostramos o nome do input em negrito.
    //   - ``cross_link``: ``related_input`` e' ``null`` e o backend
    //     popula ``related_xlink``. Mostramos o nome do cruzamento
    //     em negrito — o consultor sabe qual xlink tem pergunta
    //     pendente sem precisar abrir cada input participante.
    //
    // Sem este branch para ``related_xlink``, decisoes de
    // cross-link viravam orfas na timeline ("Pergunta em aberto"
    // sem entidade), e o consultor perdia o contexto.
    if (ev.related_input) {
      const inputName =
        ev.related_input_label || metaFallback(ev.related_input || "");
      return {
        inputName,
        whatSuffix: `— ${String(ev.label || "").toLowerCase()}`,
      };
    }
    if (ev.related_xlink) {
      const xlinkName =
        ev.related_xlink_label || metaFallback(ev.related_xlink || "");
      return {
        inputName: xlinkName,
        whatSuffix: `— ${String(ev.label || "").toLowerCase()}`,
      };
    }
  }
  return { inputName: null, whatSuffix: ev.label };
}

function whoForTimelineEvent(ev) {
  // Eventos de sistema (parsing/cruzamento/bloco) nao exibem ``who`` —
  // o sufixo ja deixa claro o que rodou. Apenas eventos com agente
  // humano relevante (decisao respondida) mantem o ``who``. Para
  // ``input_uploaded`` o ``who`` ja entra no proprio sufixo ("enviado
  // por X").
  if (ev.type === "input_uploaded") return null;
  if (ev.type === "input_parsed") return null;
  if (ev.type === "xlink_run") return null;
  if (ev.type === "block_run") return null;
  return ev.who || null;
}

// Formato compacto "19/mai 23:33" (pt-BR) — duas linhas sao ruins
// visualmente, e o coluna de tempo no grid ``classes.event`` tem
// 76px. Construimos manualmente porque ``toLocaleString`` (a) usa
// virgulas / "de" / "AM/PM" dependendo do locale, (b) ignorava o
// locale do i18n quando chamado com ``undefined`` (caia no idioma
// do browser, daí o relato "May 19, 09:12 AM" mesmo com front em
// portugues).
function timeLabelFromIso(iso, locale) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, "0");
    const monthShort = d
      .toLocaleDateString(locale, { month: "short" })
      .replace(/\.$/, "")
      .toLowerCase();
    const hour = String(d.getHours()).padStart(2, "0");
    const minute = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${monthShort} ${hour}:${minute}`;
  } catch (_) {
    return iso;
  }
}

function adaptTimelinePayload(events, locale) {
  if (!Array.isArray(events) || events.length === 0) return [];
  // Backend devolve por ordem cronologica crescente; o pane renderiza
  // de cima pra baixo seguindo a ordem do array. Os mocks usavam
  // "futuro -> agora -> passado"; o real vem em ordem natural,
  // entao invertemos para o usuario ver o mais recente primeiro.
  const sorted = [...events].sort((a, b) =>
    String(b.timestamp).localeCompare(String(a.timestamp)),
  );
  return sorted.map((ev, idx) => {
    const { inputName, whatSuffix } = labelForTimelineEvent(ev);
    return {
      kind: "past",
      category: categoryForType(ev.type),
      time: timeLabelFromIso(ev.timestamp, locale),
      who: whoForTimelineEvent(ev),
      inputName,
      what: whatSuffix,
      dot: dotForSeverity(ev.state, ev.severity, ev.superseded_by_id),
      superseded: !!ev.superseded_by_id,
      // Onda 1.4: id estavel entre re-renders/refetches. Antes
      // incluiamos ``idx`` (posicao no array ordenado), entao quando
      // um novo evento chegava no topo TODOS os ids posteriores
      // mudavam — React descartava + remontava cada item, perdendo
      // focus, transitions e qualquer estado local. Agora o id
      // deriva do ``type`` + ``timestamp`` + entidade associada,
      // que sao estaveis pelo tempo de vida do evento. Mantemos
      // ``idx`` so como tie-breaker para o caso raro de dois
      // eventos com mesmo trio (mesma transacao do backend).
      // Onda 5.x: decisoes ``scope='cross_link'`` tem
      // ``related_input=null`` e populam ``related_xlink``; usamos
      // o que estiver disponivel como discriminador da entidade.
      id: `${ev.type}|${ev.timestamp || ""}|${ev.related_input || ev.related_xlink || ""}|${idx}`,
    };
  });
}

const TimelinePane = ({ events, year, month }) => {
  const { i18n } = useTranslation();
  const { clientColor } = useThemeColors();
  const classes = useProgressStyles();
  const overlayScroll = useOverlayScrollStyles();
  const scrollRef = useNativeWheelScroll();
  const clientColorSoft = blendColors(clientColor, whiteColor, 0.08);

  // Fase 5.2: substitui MOCK_EVENTS pelo endpoint real
  // ``GET /api/v3/timeline/?year=&month=`` (``InputsV3TimelineView``).
  // Quando o caller passa ``events`` explicitamente (ex.: storybook),
  // honramos. Caso contrario, buscamos do backend.
  // Quando ``events`` vem implicito via ``progressOverview``, buscamos
  // aqui e precisamos de um gatilho explicito ao "Tentar novamente".
  const [reloadKey, setReloadKey] = useState(0);
  const refreshTimeline = useCallback(() => setReloadKey((x) => x + 1), []);

  const isRemoteFetch = events === undefined;
  const [fetched, setFetched] = useState(() => ({
    loading:
      typeof events === "undefined" &&
      Number.isFinite(year) &&
      Number.isFinite(month),
    events: [],
    error: null,
  }));

  useEffect(() => {
    if (events !== undefined) return undefined;
    if (!year || !month) {
      setFetched({ loading: false, events: [], error: null });
      return undefined;
    }
    let cancelled = false;
    setFetched({ loading: true, events: [], error: null });
    fetchInputsV3Timeline({ year, month })
      .then((data) => {
        if (cancelled) return;
        setFetched({
          loading: false,
          events: data?.events || [],
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setFetched({ loading: false, events: [], error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [events, year, month, reloadKey]);

  const baseList = useMemo(() => {
    if (events !== undefined) return events;
    return adaptTimelinePayload(fetched.events, i18n.language);
  }, [events, fetched.events, i18n.language]);

  // Filtro single-select (excludente). Clicar em um chip ativa essa
  // categoria e desativa as demais. Clicar no chip ja ativo nao
  // desliga — sempre ha exatamente uma categoria visivel.
  // Default: ``inputs`` (o consultor abre a timeline principalmente
  // para entender quem enviou ou o que foi reaproveitado).
  const [activeFilter, setActiveFilter] = useState("inputs");

  const list = useMemo(
    () =>
      (baseList || []).filter((e) => {
        if (e.kind !== "past") return true;
        return (e.category || "blocks") === activeFilter;
      }),
    [baseList, activeFilter],
  );

  // Contador no header: somente eventos reais (``kind === "past"``).
  // Marcadores ``now`` ("agora") e ``future-waiting`` ("aguardando ...")
  // sao auxiliares de leitura da timeline e nao representam eventos
  // ocorridos — portanto nao devem aparecer no contador "X eventos".
  const realEventCount = useMemo(
    () => (list || []).filter((e) => e.kind === "past").length,
    [list],
  );
  // O baseList sempre traz pelo menos o marcador "now" (ver
  // ``InputsV3ProgressOverviewView`` no backend), entao nao podemos
  // tratar ``baseList.length === 0`` como vazio. O criterio de "sem
  // eventos" e' a ausencia de eventos do tipo ``past`` no baseList.
  const hasAnyPastEvent = useMemo(
    () => (baseList || []).some((e) => e.kind === "past"),
    [baseList],
  );

  const filterChips = (
    <div
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        marginTop: 6,
      }}
    >
      {FILTERS.map((f) => {
        const active = activeFilter === f.id;
        return (
          <button
            type="button"
            key={f.id}
            onClick={() => setActiveFilter(f.id)}
            style={{
              padding: "3px 10px",
              borderRadius: 12,
              fontSize: 11,
              fontFamily: "inherit",
              cursor: "pointer",
              border: `1px solid ${active ? clientColor : grayColor[5]}`,
              background: active
                ? blendColors(clientColor, whiteColor, 0.12)
                : "transparent",
              color: active ? clientColor : grayColor[1],
              transition: "all 120ms ease",
            }}
          >
            {f.label}
          </button>
        );
      })}
    </div>
  );

  if (isRemoteFetch && fetched.loading) {
    return (
      <DrawerPaneLoadingLayout
        variant="progress"
        title="Linha do tempo"
        message="Carregando linha do tempo…"
      />
    );
  }

  if (isRemoteFetch && fetched.error) {
    return (
      <>
        <div className={classes.paneHead}>
          <h4 className={classes.paneHeadTitle}>Linha do tempo</h4>
        </div>
        <div
          ref={scrollRef}
          className={`${overlayScroll.overlayScroll} ${classes.events}`}
          style={{ flex: 1, minHeight: 0, padding: "12px 16px" }}
        >
          <PaneErrorBanner
            title="Não foi possível carregar a linha do tempo deste mês"
            message={
              "Verifique sua conexão ou tente novamente em alguns instantes; " +
              "se persistir, acione a Integration."
            }
            detail={
              fetched.error?.response?.data?.error_code ||
              fetched.error?.message ||
              null
            }
            onRetry={refreshTimeline}
            retrying={fetched.loading}
          />
        </div>
      </>
    );
  }

  if (!hasAnyPastEvent) {
    return (
      <>
        <div className={classes.paneHead}>
          <h4 className={classes.paneHeadTitle}>Linha do tempo</h4>
        </div>
        <div className={classes.paneEmpty}>
          <Schedule />
          <div className={classes.paneEmptyTitle}>
            Nenhum evento neste mês ainda
          </div>
          <div className={classes.paneEmptySub}>
            Eventos de processamento (cálculos, decisões, recálculos) aparecem
            aqui assim que acontecerem.
          </div>
        </div>
      </>
    );
  }

  // Pluralizacao simples — "1 evento" / "N eventos".
  const countLabel = `${realEventCount} ${realEventCount === 1 ? "evento" : "eventos"}`;

  return (
    <>
      <div
        className={classes.paneHead}
        style={{ flexDirection: "column", alignItems: "flex-start" }}
      >
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <h4 className={classes.paneHeadTitle}>Linha do tempo</h4>
          <span className={classes.paneHeadCount}>{countLabel}</span>
        </div>
        {filterChips}
      </div>
      <div
        ref={scrollRef}
        className={`${overlayScroll.overlayScroll} ${classes.events}`}
        style={{ flex: 1, minHeight: 0 }}
      >
        {list.map((e, i) => (
          <EventItem
            key={i}
            classes={classes}
            event={e}
            clientColor={clientColor}
            clientColorSoft={clientColorSoft}
          />
        ))}
      </div>
    </>
  );
};

TimelinePane.propTypes = {
  events: PropTypes.array,
  year: PropTypes.number,
  month: PropTypes.number,
};

export default TimelinePane;
